import React, { type ReactNode, useCallback } from "react";
import { useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import { CREATE_MERCHANT_GROUP_VIEW_PATH } from "./CreateMerchantGroupView";
import { ContentHeader } from "src/app/ui/authenticated/ContentHeader";
import { ContentHeaderControls } from "src/app/ui/authenticated/ContentHeaderControls";
import { ContentHeading } from "src/app/ui/authenticated/ContentHeading";
import { MerchantGroupTable } from "src/merchantgroups/components/MerchantGroupTable";
import {
  MerchantGroupCreate,
  MerchantGroupLabel,
} from "src/merchantgroups/formatters/MerchantGroupFormatters";
import { useMerchantGroups } from "src/merchantgroups/hooks/MerchantGroupHooks";
import type { MerchantGroup } from "src/merchantgroups/objects/MerchantGroup";
import { MerchantTable } from "src/merchants/components/MerchantTable";
import { useMerchantsSearch } from "src/merchants/hooks/useMerchantsSearch";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";
import { SearchForm } from "src/shared/components/SearchForm";
import { useQueryParamState } from "src/shared/hooks/queryparams/useQueryParamState";

export const MERCHANT_GROUPS_VIEW_PATH = "/mg";

export interface MerchantGroupsViewProps {
  children?: (merchantGroups: MerchantGroup[], onLoad: () => void) => ReactNode;
}

export const MerchantGroupsView = ({ children }: MerchantGroupsViewProps) => {
  const [term, setTerm] = useQueryParamState("term");
  const mode = term === null ? "list" : "search";

  const merchantGroupsResult = useMerchantGroups();
  const merchantsResult = useMerchantsSearch(term ?? "", {
    enabled: mode === "search",
  });

  const queryClient = useQueryClient();
  const handleLoad = useCallback(() => {
    void queryClient.invalidateQueries("merchantGroups");
    void queryClient.invalidateQueries("merchants");
  }, [queryClient]);

  return (
    <>
      <ContentHeader>
        <ContentHeaderControls>
          <Link to={CREATE_MERCHANT_GROUP_VIEW_PATH}>
            <Button color="info">
              <MerchantGroupCreate />
            </Button>
          </Link>
        </ContentHeaderControls>

        <ContentHeading>
          <Row>
            <Col sm={12} lg={6}>
              <h2>
                <MerchantGroupLabel />
              </h2>
            </Col>
            <Col sm={12} lg={6}>
              <SearchForm value={term} onUpdate={setTerm} />
            </Col>
          </Row>
        </ContentHeading>
      </ContentHeader>

      <ReactQueryResult {...merchantGroupsResult}>
        {(merchantGroups) => (
          <>
            {children && children(merchantGroups, handleLoad)}

            {mode === "list" && (
              <MerchantGroupTable merchantGroups={merchantGroups} />
            )}
          </>
        )}
      </ReactQueryResult>

      {mode === "search" && (
        <ReactQueryResult {...merchantsResult}>
          {(merchants) => <MerchantTable merchants={merchants} />}
        </ReactQueryResult>
      )}
    </>
  );
};

export default MerchantGroupsView;
